import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Upload, Download, Trash2, Loader2 } from "lucide-react";
import { api, errMsg, downloadFile, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/admin/ui";

export default function CaseAttachments({ c, locked, onChange }: { c: any; locked: boolean; onChange: (data: any) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [searchId, setSearchId] = useState("");
  const [busy, setBusy] = useState(false);
  const SIGNED = ["state_authorization", "payment_authorization"];
  const atts = (c.attachments || []).filter((a: any) => !a.is_deleted && !SIGNED.includes(a.kind));
  const signedDocs = (c.attachments || []).filter((a: any) => !a.is_deleted && SIGNED.includes(a.kind));
  const byId = Object.fromEntries((c.searches || []).map((s: any) => [s.id, s]));

  const upload = async (file: File) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/cases/${c.id}/attachments`, fd, { params: { search_id: searchId || undefined } });
      onChange(data);
      toast.success("Attachment added");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  const remove = async (id: string) => {
    try {
      onChange((await api.delete(`/admin/cases/${c.id}/attachments/${id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
    {signedDocs.length > 0 && (
      <Card title="Signed authorizations" testId="case-signed-docs-card" actions={<span className="text-xs text-[#94A3B8]">Kept on file, not part of the report</span>}>
        <div className="space-y-2 p-5">
          {signedDocs.map((a: any) => (
            <div key={a.id} className="flex items-center gap-3 rounded-xl bg-[#ECFDF5] px-4 py-3 text-sm" data-testid={`signed-doc-${a.id}`}>
              <Paperclip className="h-4 w-4 text-[#0F766E]" />
              <div className="min-w-0 flex-1"><p className="truncate font-semibold text-[#1A2536]">{a.filename}</p><p className="text-xs text-[#64748B]">Signed by applicant · {fmtDateTime(a.at)}</p></div>
              <button className="btn-secondary btn-sm" onClick={() => downloadFile(`/admin/cases/${c.id}/attachments/${a.id}`, a.filename)} data-testid={`signed-doc-download-${a.id}`}><Download className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </Card>
    )}
    <Card title="Attachments (court records)" testId="case-attachments-card" actions={<span className="text-xs text-[#94A3B8]">Appended to the final report</span>}>
      <div className="space-y-3 p-5">
        {atts.map((a: any, i: number) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-[#F8F7F3] px-4 py-3 text-sm" data-testid={`attachment-${a.id}`}>
            <Paperclip className="h-4 w-4 text-[#1B365D]" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-[#1A2536]">Attachment {i + 1}: {a.filename}</p>
              <p className="text-xs text-[#94A3B8]">{byId[a.search_id]?.type || "General"} · {a.uploaded_by} · {fmtDateTime(a.at)}</p>
            </div>
            <button className="btn-secondary btn-sm" onClick={() => downloadFile(`/admin/cases/${c.id}/attachments/${a.id}`, a.filename)} data-testid={`attachment-download-${a.id}`}><Download className="h-4 w-4" /></button>
            {!locked && <button className="btn-secondary btn-sm text-[#B91C1C]" onClick={() => remove(a.id)} data-testid={`attachment-remove-${a.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {!locked && (
          <div className="flex flex-wrap gap-2">
            <select className="field-input h-10 flex-1 text-sm" value={searchId} onChange={(e) => setSearchId(e.target.value)} data-testid="attachment-search-select">
              <option value="">Related search (optional)</option>
              {(c.searches || []).map((s: any) => <option key={s.id} value={s.id}>{s.type}</option>)}
            </select>
            <input ref={ref} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" data-testid="attachment-file-input" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <button className="btn-secondary btn-sm h-10" disabled={busy} onClick={() => ref.current?.click()} data-testid="attachment-upload-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload PDF / image
            </button>
          </div>
        )}
      </div>
    </Card>
    </>
  );
}
